
import { useRef, useEffect, useMemo, useState } from 'react';
import { toast } from "@/components/ui/use-toast";
import { calculateChartHeight, createChartRows, getChartOptions } from './utils/chartUtils';
// createTooltipFormatter no longer used; tooltips are built inline below.
import { applyChartStyles, enhanceChartDOM } from './components/ChartStyles';
import { ProjectTask } from '../types';
import { ProjectSubtask } from '../task-management/types';
// dateUtils import kept for potential future use

interface GanttChartProps {
  selectedProject: any;
  chartLoaded: boolean;
  onTaskSelect: (task: any, index: number) => void;
  isRestrictedUser?: boolean;
  subtasks?: ProjectSubtask[];
  taskNameToUuid?: Record<string, string>;
  defaultExpandSubtasks?: boolean;
}

const GanttChart = ({ selectedProject, chartLoaded, onTaskSelect, isRestrictedUser = false, subtasks = [], taskNameToUuid = {}, defaultExpandSubtasks = false }: GanttChartProps) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const triangleClickPendingRef = useRef<boolean>(false);
  const mouseDownHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const didInitExpandRef = useRef<string | null>(null);

  // Auto-expand all parent tasks with subtasks for non-Director roles
  useEffect(() => {
    if (!defaultExpandSubtasks) return;
    if (!selectedProject || !selectedProject.tasks) return;
    const projectKey = `${selectedProject.id || selectedProject.name || 'p'}`;
    if (didInitExpandRef.current === projectKey) return;
    if (!subtasks || subtasks.length === 0) return;

    const namesWithSubtasks = new Set<string>();
    subtasks.forEach((s) => {
      const n = s.task_name || Object.entries(taskNameToUuid).find(([, uuid]) => uuid === s.task_id)?.[0];
      if (n) namesWithSubtasks.add(n);
    });
    const idsToExpand = new Set<string>();
    (selectedProject.tasks as ProjectTask[]).forEach((task, index) => {
      if (namesWithSubtasks.has(task.name)) {
        idsToExpand.add(task.taskId || `task-${index}`);
      }
    });
    if (idsToExpand.size > 0) {
      didInitExpandRef.current = projectKey;
      setExpandedTasks(idsToExpand);
    }
  }, [defaultExpandSubtasks, selectedProject, subtasks, taskNameToUuid]);

  // Draw the chart when selected project or chart loaded status changes
  useEffect(() => {
    if (chartLoaded && selectedProject && selectedProject.tasks && selectedProject.tasks.length > 0 && chartContainerRef.current) {
      console.log('Drawing chart with project:', selectedProject.name);
      console.log('Tasks count:', selectedProject.tasks.length);
      setTimeout(() => {
        drawChart();
      }, 200); // Add a slight delay to ensure the DOM is fully ready
    }
  }, [selectedProject, chartLoaded, subtasks, expandedTasks]);

  // Function to get task display name with Jira integration
  const getTaskDisplayName = (task: ProjectTask): string => {
    let displayName = task.name;
    
    if (task.jiraIssueKey) {
      displayName = `${task.name} [${task.jiraIssueKey}]`;
    }
    
    if (task.jiraStatus) {
      displayName = `${displayName} (${task.jiraStatus})`;
    }
    
    return displayName;
  };

  // Function to get task color based on Jira status
  const getTaskColor = (task: ProjectTask, index: number): string => {
    // Define a vibrant color palette for resources
    const colorPalette = [
      { color: '#5D87FF', dark: '#4A6FCF', light: '#7A9EFF' },
      { color: '#FF8C00', dark: '#E67300', light: '#FFA333' },
      { color: '#28C76F', dark: '#1EA05A', light: '#4DD38A' },
      { color: '#FF6B6B', dark: '#E04F4F', light: '#FF8C8C' },
      { color: '#605BFF', dark: '#4A46E6', light: '#7A75FF' },
      { color: '#00BCD4', dark: '#0097A7', light: '#33C9D7' },
      { color: '#FF9F43', dark: '#E6802A', light: '#FFAD66' },
      { color: '#9C27B0', dark: '#7B1FA2', light: '#B52FD1' }
    ];

    if (task.jiraStatus) {
      switch (task.jiraStatus) {
        case 'Done':
        case 'Closed':
        case 'Resolved':
          return '#28C76F'; // Green for completed
        case 'In Progress':
        case 'In Development':
          return '#FF8C00'; // Orange for in progress
        case 'To Do':
        case 'Open':
        case 'Backlog':
          return '#FF6B6B'; // Red for not started
        case 'Code Review':
        case 'In Review':
        case 'Testing':
        case 'QA':
          return '#605BFF'; // Purple for review/testing
        default:
          return colorPalette[index % colorPalette.length].color;
      }
    }
    
    return colorPalette[index % colorPalette.length].color;
  };

  // Function to draw the Google Gantt Chart
  const drawChart = () => {
    if (!selectedProject || !selectedProject.tasks || !selectedProject.tasks.length || !chartContainerRef.current || !window.google || !window.google.visualization) {
      console.error('Unable to draw chart, missing required elements', {
        hasSelectedProject: !!selectedProject,
        hasTasks: !!(selectedProject && selectedProject.tasks),
        tasksLength: selectedProject?.tasks?.length || 0,
        hasChartContainer: !!chartContainerRef.current,
        hasGoogle: !!window.google,
        hasVisualization: !!(window.google && window.google.visualization)
      });
      return;
    }

    try {
      console.log('Creating data table');
      const data = new window.google.visualization.DataTable();
      
      // Add columns
      data.addColumn('string', 'Task ID');
      data.addColumn('string', 'Task Name');
      data.addColumn('string', 'Resource');
      data.addColumn('date', 'Start Date');
      data.addColumn('date', 'End Date');
      data.addColumn('number', 'Duration');
      data.addColumn('number', 'Percent Complete');
      data.addColumn('string', 'Dependencies');
      data.addColumn({ type: 'string', role: 'style' }); // Style column for milestone/subtask colors
      data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); // Custom HTML tooltip

      // Format a Date as DDMMMYY (e.g. 21Aug25)
      const fmtDDMMMYY = (d: Date): string => {
        if (!d || isNaN(d.getTime())) return '';
        const day = String(d.getDate()).padStart(2, '0');
        const mon = d.toLocaleString('en-US', { month: 'short' });
        const yr = String(d.getFullYear()).slice(-2);
        return `${day}${mon}${yr}`;
      };

      const buildTooltip = (
        title: string,
        resourceLabel: string,
        resourceValue: string,
        start: Date,
        end: Date,
        duration: number | null,
        progress: number
      ): string => {
        return (
          '<div style="padding:10px;background:#000;color:#fff;border:1px solid #333;border-radius:4px;max-width:320px;font-family:Inter,system-ui,sans-serif;">' +
          '<div style="font-weight:bold;margin-bottom:6px;font-size:13px;">' + title + '</div>' +
          '<div style="font-size:12px;margin-bottom:2px;">' + resourceLabel + ': ' + resourceValue + '</div>' +
          '<div style="font-size:12px;margin-bottom:2px;">Start: ' + fmtDDMMMYY(start) + '</div>' +
          '<div style="font-size:12px;margin-bottom:2px;">End: ' + fmtDDMMMYY(end) + '</div>' +
          (duration !== null ? '<div style="font-size:12px;margin-bottom:2px;">Duration: ' + duration + ' days</div>' : '') +
          '<div style="font-size:12px;">Progress: ' + progress + '%</div>' +
          '</div>'
        );
      };
      
      // Use project start date or first task start date
      const projectStartDate = selectedProject.startDate ? 
        new Date(selectedProject.startDate) : 
        (selectedProject.tasks.length > 0 && selectedProject.tasks[0].startDate ? new Date(selectedProject.tasks[0].startDate) : new Date());
      
      // Use task dates as stored in the project - no recalculation needed
      // Dates are already correctly calculated and saved by the Edit Project and Edit Task dialogs
      const excludeWeekends = selectedProject.excludeWeekends ?? true;
      let tasksWithDates = selectedProject.tasks as ProjectTask[];

      // Create dependency mapping to ensure IDs are correctly referenced
      const taskIdMap: Record<string, boolean> = {};
      if (Array.isArray(tasksWithDates)) {
        tasksWithDates.forEach((task: ProjectTask, index: number) => {
          // Use the task's taskId if available, otherwise fallback to the generic ID
          const taskIdForMap = task.taskId || `task-${index}`;
          taskIdMap[taskIdForMap] = true;
        });
      }
      
      // Create data rows for the chart - modified for milestones
      const rows: any[][] = [];
      // Track which rows are subtasks so click handler can ignore them
      const rowMeta: Array<{ kind: 'task' | 'subtask' | 'parent'; taskIndex?: number; parentTaskId?: string }> = [];

      // Build subtasks-by-task map. project_subtasks.task_id is project_tasks.id (UUID).
      // Map task-by-name → UUID via taskNameToUuid prop.
      const subtasksByTaskName: Record<string, ProjectSubtask[]> = {};
      (subtasks || []).forEach((s) => {
        const name = s.task_name || Object.entries(taskNameToUuid).find(([, uuid]) => uuid === s.task_id)?.[0];
        if (!name) return;
        if (!subtasksByTaskName[name]) subtasksByTaskName[name] = [];
        subtasksByTaskName[name].push(s);
      });

      // Group tasks by phase for visual separation
      const tasksByPhase: Record<string, any[]> = {};
      
      // First pass: group tasks by phase
      if (Array.isArray(tasksWithDates)) {
        tasksWithDates.forEach((task: ProjectTask, index: number) => {
          const phase = task.phase || "Main Tasks";
          if (!tasksByPhase[phase]) {
            tasksByPhase[phase] = [];
          }
          tasksByPhase[phase].push({ ...task, originalIndex: index });
        });
      }
      
      // Second pass: create rows by phase
      Object.entries(tasksByPhase).forEach(([phase, tasks]) => {
        if (Array.isArray(tasks)) {
          tasks.forEach((task) => {
            const index = task.originalIndex;
            const taskId = task.taskId || `task-${index}`;
            const resourceName = `Resource-${index % 8}`;
            const startDate = task.startDate ? new Date(task.startDate) : new Date(projectStartDate);
            let endDate = task.endDate ? new Date(task.endDate) : startDate;
            const dependencies = processTaskDependencies(task, index, taskIdMap);
            const isMilestone = task.isMilestone || task.duration === 0;
            if (isMilestone) {
              endDate = new Date(startDate);
            }
            let styleColumn: string | null = null;
            if (isMilestone) {
              styleColumn = 'fill-color: #FF5252; stroke-width: 50';
            } else if (task.jiraStatus) {
              const taskColor = getTaskColor(task, index);
              styleColumn = `fill-color: ${taskColor}; stroke-color: ${taskColor}`;
            }

            // Determine whether this task has subtasks defined
            const taskSubtasks = subtasksByTaskName[task.name] || [];
            const hasSubtasks = taskSubtasks.length > 0;
            const isExpanded = expandedTasks.has(taskId);

            let displayName = getTaskDisplayName(task);
            if (hasSubtasks) {
              displayName = `${isExpanded ? '▼' : '▶'} ${displayName}`;
            }

            // For parent tasks, count unique resources across subtasks for tooltip
            const uniqueResourceCount = hasSubtasks
              ? new Set(
                  taskSubtasks
                    .map((s) => (s.name || s.resource_id || '').trim())
                    .filter((v) => v.length > 0)
                ).size
              : 0;

            const parentTooltip = hasSubtasks
              ? buildTooltip(
                  getTaskDisplayName(task),
                  'Unique resources',
                  String(uniqueResourceCount),
                  startDate,
                  endDate,
                  isMilestone ? null : (task.duration ?? null),
                  task.progress || 0
                )
              : buildTooltip(
                  getTaskDisplayName(task),
                  'Resource',
                  isMilestone ? 'Milestone' : resourceName,
                  startDate,
                  endDate,
                  isMilestone ? null : (task.duration ?? null),
                  task.progress || 0
                );

            rows.push([
              taskId,
              displayName,
              isMilestone ? 'Milestone' : resourceName,
              startDate,
              endDate,
              isMilestone ? null : (task.duration || null),
              task.progress || 0,
              dependencies,
              styleColumn,
              parentTooltip
            ]);
            rowMeta.push({ kind: hasSubtasks ? 'parent' : 'task', taskIndex: index });

            if (hasSubtasks && isExpanded) {
              const sortedSubs = [...taskSubtasks].sort((a, b) => {
                const aDate = a.start_date || '';
                const bDate = b.start_date || '';
                if (aDate !== bDate) return aDate.localeCompare(bDate);
                return (a.step || 0) - (b.step || 0);
              });
              sortedSubs.forEach((sub) => {
                if (!sub.start_date || !sub.end_date) {
                  console.warn('Skipping subtask without dates:', sub.subtask_name);
                  return;
                }
                const sStart = new Date(sub.start_date);
                let sEnd = new Date(sub.end_date);
                if (sEnd < sStart) sEnd = new Date(sStart);
                const dayMs = 1000 * 60 * 60 * 24;
                const sDuration = Math.max(1, Math.round((sEnd.getTime() - sStart.getTime()) / dayMs));
                const progress = sub.status === 'Done' ? 100 : sub.status === 'In Progress' ? 50 : 0;
                const subStyle = `fill-color: #B388FF; stroke-color: #7C4DFF; opacity: 0.85`;
                const assignedResource = (sub.name && sub.name.trim()) || 'Unassigned';
                const subTooltip = buildTooltip(
                  sub.subtask_name,
                  'Resource',
                  assignedResource,
                  sStart,
                  sEnd,
                  sDuration,
                  progress
                );
                rows.push([
                  `${taskId}::sub::${sub.id}`,
                  `       └ ${sub.subtask_name}`,
                  assignedResource,
                  sStart,
                  sEnd,
                  sDuration,
                  progress,
                  taskId,
                  subStyle,
                  subTooltip
                ]);
                rowMeta.push({ kind: 'subtask', parentTaskId: taskId });
              });
            }
          });
        }
      });
      
      // Add all rows to the data table
      data.addRows(rows);
      
      // Calculate chart height based on total visible rows
      const taskCount = rows.length;

      const rowHeight = 40; // Height per task row
      const headerHeight = 0; // Removed extra header padding
      const dateBarHeight = 25; // Reduced date bar height

      // Dynamic chart height calculation - more precise to eliminate whitespace
      const chartHeight = (taskCount * rowHeight) + dateBarHeight + headerHeight;
      
      // Set chart options with enhanced styling for milestones and increased label width + right margin
      const options = {
        backgroundColor: '#1A1F2C',
        height: chartHeight,
        gantt: {
          trackHeight: 38,
          barHeight: 30,
          defaultStartDate: projectStartDate,
          criticalPathEnabled: true,
          criticalPathStyle: {
            stroke: '#e64a19',
            strokeWidth: 3
          },
          arrow: {
            angle: 100,
            width: 2,
            color: 'darkgrey',
            radius: 0
          },
          labelMaxWidth: 350, // Increased to accommodate Jira information
          labelStyle: {
            fontName: 'Arial',
            fontSize: 12
          },
          innerGridHorizLine: {
            stroke: '#45496a',
            strokeWidth: 0.5
          },
          innerGridTrack: {
            fill: '#1a1f2c'
          },
          innerGridDarkTrack: {
            fill: '#232736'
          }
        },
        tooltip: {
          isHtml: true,
          trigger: 'focus'
        },
        // Add right margin to ensure spacing after the last task
        chartArea: {
          left: 0,
          top: 0,
          width: '95%',  // Reduced from 100% to add right margin
          height: '100%'
        }
      };

      console.log('Creating chart instance');
      
      // Create and draw the chart
      const chart = new window.google.visualization.Gantt(chartContainerRef.current);

      // Tooltip HTML is already embedded directly in the data rows above.
      const formattedData = data;
      
      // Add click event listener
      window.google.visualization.events.addListener(chart, 'select', () => {
        const selection = chart.getSelection();
        if (!selection || selection.length === 0) return;
        const rowIndex = selection[0].row;
        if (rowIndex === null || rowIndex === undefined) return;
        const taskData = rows[rowIndex];
        const meta = rowMeta[rowIndex];
        if (!taskData || !meta) return;
        const taskId = taskData[0] as string;

        // Subtask row: ignore
        if (meta.kind === 'subtask') {
          chart.setSelection([]);
          triangleClickPendingRef.current = false;
          return;
        }

        // Parent task with subtasks: triangle click → toggle only;
        // body/label click → open Edit dialog only.
        if (meta.kind === 'parent') {
          const wasTriangle = triangleClickPendingRef.current;
          triangleClickPendingRef.current = false;
          chart.setSelection([]);
          if (wasTriangle) {
            setExpandedTasks((prev) => {
              const next = new Set(prev);
              if (next.has(taskId)) next.delete(taskId);
              else next.add(taskId);
              return next;
            });
            return;
          }
          if (isRestrictedUser) return;
          const idx = meta.taskIndex ?? -1;
          if (idx >= 0 && selectedProject.tasks && idx < selectedProject.tasks.length) {
            onTaskSelect(selectedProject.tasks[idx], idx);
          }
          return;
        }

        // Plain task: open edit dialog (only for non-restricted users)
        triangleClickPendingRef.current = false;
        if (isRestrictedUser) return;
        let taskIndex: number;
        if (taskId.startsWith('T')) {
          const foundTaskIndex = selectedProject.tasks.findIndex((t: ProjectTask) => t.taskId === taskId);
          taskIndex = foundTaskIndex >= 0 ? foundTaskIndex : 0;
        } else {
          taskIndex = parseInt(taskId.split('-')[1]);
        }
        if (!isNaN(taskIndex) && selectedProject.tasks && taskIndex < selectedProject.tasks.length) {
          onTaskSelect(selectedProject.tasks[taskIndex], taskIndex);
        }
      });

      // Detect whether a click landed on the leading ▶/▼ glyph of a label.
      const containerEl = chartContainerRef.current;
      if (mouseDownHandlerRef.current && containerEl) {
        containerEl.removeEventListener('mousedown', mouseDownHandlerRef.current, true);
      }
      const onMouseDownCapture = (e: MouseEvent) => {
        triangleClickPendingRef.current = false;
        const target = e.target as Element | null;
        if (!target) return;
        const textEl = target.closest('text') as SVGTextElement | null;
        if (!textEl) return;
        const txt = textEl.textContent || '';
        if (!txt.startsWith('▶') && !txt.startsWith('▼')) return;
        try {
          const bbox = textEl.getBoundingClientRect();
          if (e.clientX - bbox.left <= 16) {
            triangleClickPendingRef.current = true;
          }
        } catch {
          // If geometry fails, default to body-click behavior.
        }
      };
      mouseDownHandlerRef.current = onMouseDownCapture;
      containerEl?.addEventListener('mousedown', onMouseDownCapture, true);
      
      // Add custom event listeners for hover effects and whitespace removal
      window.google.visualization.events.addListener(chart, 'ready', () => {
        console.log('Chart ready event triggered');
        
        // Apply custom CSS and style elements
        applyChartStyles(chartContainerRef, chartHeight, selectedProject);
        
        // Apply direct DOM manipulation for better chart appearance
        enhanceChartDOM(chartContainerRef, chartHeight, selectedProject);
      });
      
      console.log('Drawing chart');
      chart.draw(formattedData, options);
      
    } catch (error) {
      console.error("Error drawing Gantt chart:", error);
      toast({
        title: "Error",
        description: "Failed to draw Gantt chart: " + (error as Error).message,
        variant: "destructive"
      });
    }
  };

  // Helper function for processing task dependencies (moved from chartUtils for clarity)
  const processTaskDependencies = (
    task: any, 
    index: number, 
    taskIdMap: Record<string, boolean>
  ): string | null => {
    let dependencies = null;
    
    if (task.dependencies) {
      if (typeof task.dependencies === 'string') {
        // Handle comma-separated string of dependencies
        const deps = task.dependencies.split(',').filter((dep: string) => dep.trim() !== '');
        // Verify each dependency exists and create dependency string
        if (deps.length > 0) {
          const validDeps = deps.filter((dep: string) => taskIdMap[dep.trim()]);
          if (validDeps.length > 0) {
            dependencies = validDeps.join(',');
          }
        }
      } else if (Array.isArray(task.dependencies)) {
        // Handle array of dependencies
        const validDeps = task.dependencies.filter((dep: string) => taskIdMap[dep]);
        if (validDeps.length > 0) {
          dependencies = validDeps.join(',');
        }
      }
    }
    
    return dependencies;
  };

  return (
    <div 
      ref={chartContainerRef} 
      className="w-full" 
      style={{
        margin: 0, 
        padding: 0, 
        overflow: 'hidden'
      }}
    ></div>
  );
};

export default GanttChart;
